import React, { useState, useEffect } from "react";
import { db } from "../../firebase";
import { updateDoc, doc } from "firebase/firestore";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";

const SummaryTab = ({ trip, theme, lang = "th", onNavigate }) => {
  const [shoppingList, setShoppingList] = useState([]);
  const [isAddingShop, setIsAddingShop] = useState(false);
  const [newShopName, setNewShopName] = useState("");

  // 🌟 State สำหรับจัดการการหารบิล (แยกอิสระ ไม่ต้องอิงจากหน้าสมาชิก)
  const [isDivided, setIsDivided] = useState(true);
  const [divideBy, setDivideBy] = useState(
    trip.members?.length > 0 ? trip.members.length : 1
  );

  useEffect(() => {
    setShoppingList(trip.shoppingList || []);
  }, [trip.shoppingList]);

  const handleToggleShopping = async (id) => {
    const newList = shoppingList.map((item) =>
      item.id === id ? { ...item, isBought: !item.isBought } : item
    );
    setShoppingList(newList);
    trip.shoppingList = newList;

    try {
      if (trip.id) {
        await updateDoc(doc(db, "trips", trip.id), {
          shoppingList: newList,
        });
      }
    } catch (error) {
      console.error("Error updating shopping list:", error);
    }
  };

  const onDragEnd = async (result) => {
    if (!result.destination) return;
    const items = Array.from(sortedShoppingList);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    const updatedItems = items.map((item, index) => ({
      ...item,
      order: index,
    }));

    setShoppingList(updatedItems);
    trip.shoppingList = updatedItems;

    try {
      if (trip.id) {
        await updateDoc(doc(db, "trips", trip.id), {
          shoppingList: updatedItems,
        });
      }
    } catch (error) {
      console.error("Error updating order:", error);
    }
  };

  const handleAddShoppingItem = async (e) => {
    e.preventDefault();
    if (!newShopName.trim()) return;

    const newItem = {
      id: `shop_${Date.now()}`,
      name: newShopName.trim(),
      isBought: false,
      costTHB: 0,
      notes: "",
      link: "",
      order: shoppingList.length,
    };

    const newList = [...shoppingList, newItem];
    setShoppingList(newList);
    trip.shoppingList = newList;
    setNewShopName("");
    setIsAddingShop(false);

    try {
      if (trip.id) {
        await updateDoc(doc(db, "trips", trip.id), { shoppingList: newList });
      }
    } catch (error) {
      console.error("Error adding item:", error);
    }
  };

  const sortedShoppingList = [...shoppingList].sort((a, b) => {
    if (typeof a.order === "number" && typeof b.order === "number")
      return a.order - b.order;
    return 0;
  });

  // ─── คำนวณยอดเงิน ───
  const flightTotal = (trip.flightList || []).reduce(
    (sum, f) => sum + (Number(f.price) || 0),
    0
  );
  const stayTotal = (trip.accommodations || []).reduce(
    (sum, a) => sum + (Number(a.totalPrice) || 0),
    0
  );
  const shoppingTotal = (shoppingList || []).reduce(
    (sum, s) => sum + (Number(s.costTHB) || 0),
    0
  );
  const eatingTotal = (trip.itinerary || []).reduce(
    (sum, i) => sum + (Number(i.costTHB) || 0),
    0
  );
  const insuranceTotal = (trip.insurances || []).reduce(
    (sum, i) => sum + (Number(i.price) || 0),
    0
  );

  const expensesList = trip.expenses || [];
  const totalIncome = expensesList
    .filter((t) => t.type === "income")
    .reduce((sum, t) => sum + (Number(t.amount) || 0), 0);

  const miscExpenseTotal = expensesList
    .filter((t) => t.type === "expense")
    .reduce(
      (sum, t) => sum + (Number(t.amountPerPerson) || Number(t.amount) || 0),
      0
    );

  const sharedTotal = flightTotal + stayTotal;

  const grandTotal =
    sharedTotal +
    shoppingTotal +
    eatingTotal +
    insuranceTotal +
    miscExpenseTotal;

  const netTotal = grandTotal - totalIncome;

  const sortedFlights = [...(trip.flightList || [])].sort(
    (a, b) => new Date(a.depDate) - new Date(b.depDate)
  );

  const i18n = {
    th: {
      summary: "💰 สรุปค่าใช้จ่าย",
      sharedTitle: "ยอดรวมกองกลาง (เดินทาง + ที่พัก)",
      flightTitle: "✈️ ค่าเดินทาง",
      stayTitle: "🏨 ที่พัก",
      shoppingTitle: "🛍️ ของที่ซื้อก่อนไป",
      eatingTitle: "🍜 กิน/เที่ยว (ในทริป)",
      miscTitle: "💸 จิปาถะ / บัญชีทริป",
      grandTitle: "ยอดรวมรายจ่ายทั้งทริป",
      grandDesc: "เดินทาง + ที่พัก + ช้อปปิ้ง + กินเที่ยว + ประกัน + จิปาถะ",
      checklistTitle: "🧳 รายการเตรียมของ (Checklist)",
      flightScheduleTitle: "🗓️ ปฏิทินเที่ยวบิน",
    },
    en: {
      summary: "💰 Cost Summary",
      sharedTitle: "Shared Total (Flights + Stays)",
      flightTitle: "✈️ Flights",
      stayTitle: "🏨 Accommodation",
      shoppingTitle: "🛍️ Pre-trip Shopping",
      eatingTitle: "🍜 Food & Activities",
      miscTitle: "💸 Misc / Trip Ledger",
      grandTitle: "Total Trip Expenses",
      grandDesc: "Flights + Stays + Shopping + Food + Insurance + Misc",
      checklistTitle: "🧳 Preparation Checklist",
      flightScheduleTitle: "🗓️ Flight Schedule",
    },
  };

  const t = i18n[lang] || i18n.th;
  const getPercent = (val) =>
    grandTotal > 0 ? Math.round((val / grandTotal) * 100) : 0;

  const isDarkMode = theme?.cardBg !== "#fff";

  const cardStyle = {
    backgroundColor: theme?.cardBg || "#fff",
    border: `1px solid ${theme?.border || "#eee"}`,
    borderRadius: "12px",
    padding: "15px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.02)",
    transition: "all 0.2s ease-in-out",
  };

  const inputStyle = {
    padding: "10px 12px",
    borderRadius: "8px",
    border: `1px solid ${theme?.border || "#ccc"}`,
    backgroundColor: theme?.cardBg || "#fff",
    color: theme?.text || "#000",
    fontSize: "14px",
    outline: "none",
    width: "100%",
    boxSizing: "border-box",
  };

  const handleMouseEnter = (e, color) => {
    if (onNavigate) {
      e.currentTarget.style.borderColor = color;
      e.currentTarget.style.transform = "translateY(-3px)";
      e.currentTarget.style.boxShadow = "0 6px 15px rgba(0,0,0,0.1)";
    }
  };
  const handleMouseLeave = (e) => {
    if (onNavigate) {
      e.currentTarget.style.borderColor = theme?.border || "#eee";
      e.currentTarget.style.transform = "none";
      e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.02)";
    }
  };

  return (
    <div style={{ color: theme?.text || "#000", padding: "5px" }}>
      <h3 style={{ marginBottom: "15px", display: "flex", gap: "8px" }}>
        {t.flightScheduleTitle}
      </h3>
      <div style={{ ...cardStyle, marginBottom: "25px" }}>
        {sortedFlights.length > 0 ? (
          <div
            style={{ display: "flex", flexDirection: "column", gap: "12px" }}
          >
            {sortedFlights.map((f, idx) => {
              const dateObj = f.depDate ? new Date(f.depDate) : null;
              return (
                <div
                  key={f.id || idx}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "15px",
                    padding: "10px",
                    borderRadius: "10px",
                    backgroundColor:
                      theme?.cardBg === "#fff"
                        ? "#f8fafc"
                        : "rgba(255,255,255,0.05)",
                    border: `1px solid ${theme?.border || "#e2e8f0"}`,
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      backgroundColor: "#10b981",
                      color: "#fff",
                      borderRadius: "8px",
                      minWidth: "60px",
                      height: "60px",
                      flexShrink: 0,
                    }}
                  >
                    <span
                      style={{
                        fontSize: "12px",
                        fontWeight: "600",
                        textTransform: "uppercase",
                      }}
                    >
                      {dateObj
                        ? dateObj.toLocaleDateString(
                            lang === "th" ? "th-TH" : "en-US",
                            { month: "short" }
                          )
                        : "-"}
                    </span>
                    <span
                      style={{
                        fontSize: "22px",
                        fontWeight: "bold",
                        lineHeight: "1",
                      }}
                    >
                      {dateObj ? dateObj.getDate() : "-"}
                    </span>
                  </div>
                  <div style={{ flex: 1 }}>
                    <div
                      style={{
                        fontSize: "15px",
                        fontWeight: "bold",
                        marginBottom: "4px",
                      }}
                    >
                      {f.airline || "ไม่ระบุสายการบิน"}{" "}
                      <span style={{ color: "#888", fontSize: "13px" }}>
                        {f.flightNo}
                      </span>
                    </div>
                    <div
                      style={{
                        fontSize: "13px",
                        color: theme?.text || "#475569",
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        flexWrap: "wrap",
                      }}
                    >
                      <span>
                        🛫 <strong>{f.depCode || f.depCity || "-"}</strong> (
                        {f.depTime || "00:00"})
                      </span>
                      <span style={{ color: "#94a3b8" }}>➔</span>
                      <span>
                        🛬 <strong>{f.arrCode || f.arrCity || "-"}</strong> (
                        {f.arrTime || "00:00"})
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div
            style={{
              fontSize: "13px",
              color: "#888",
              textAlign: "center",
              padding: "10px 0",
            }}
          >
            ยังไม่มีข้อมูลเที่ยวบิน
          </div>
        )}
      </div>

      <h3 style={{ marginBottom: "15px", display: "flex", gap: "8px" }}>
        {t.checklistTitle}
      </h3>
      <div style={{ ...cardStyle, marginBottom: "25px" }}>
        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId="summary-shopping-list">
            {(provided) => (
              <div
                {...provided.droppableProps}
                ref={provided.innerRef}
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
                  gap: "10px",
                }}
              >
                {sortedShoppingList.length > 0 ? (
                  sortedShoppingList.map((item, idx) => (
                    <Draggable
                      key={String(item.id)}
                      draggableId={String(item.id)}
                      index={idx}
                    >
                      {(provided, snapshot) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          style={{
                            ...provided.draggableProps.style,
                            display: "flex",
                            alignItems: "center",
                            gap: "12px",
                            padding: "10px 14px",
                            borderRadius: "8px",
                            backgroundColor: item.isBought
                              ? theme?.cardBg === "#fff"
                                ? "#f0fdf4"
                                : "rgba(34,197,94,0.1)"
                              : theme?.cardBg === "#fff"
                              ? "#f8fafc"
                              : "rgba(255,255,255,0.03)",
                            border: `1px solid ${
                              item.isBought
                                ? "transparent"
                                : theme?.border || "#f1f5f9"
                            }`,
                            boxShadow: snapshot.isDragging
                              ? "0 5px 15px rgba(0,0,0,0.15)"
                              : "none",
                            opacity: snapshot.isDragging ? 0.8 : 1,
                            transition: "background-color 0.2s",
                          }}
                        >
                          <div
                            className="no-print"
                            {...provided.dragHandleProps}
                            style={{
                              color: "#bbb",
                              cursor: "grab",
                              display: "flex",
                              alignItems: "center",
                            }}
                          >
                            ⋮⋮
                          </div>
                          <input
                            type="checkbox"
                            checked={!!item.isBought}
                            onChange={() => handleToggleShopping(item.id)}
                            style={{
                              width: "20px",
                              height: "20px",
                              accentColor: "#10b981",
                              cursor: "pointer",
                              flexShrink: 0,
                            }}
                          />
                          <span
                            onClick={() => handleToggleShopping(item.id)}
                            style={{
                              fontSize: "15px",
                              color: item.isBought
                                ? "#10b981"
                                : theme?.text || "#000",
                              textDecoration: item.isBought
                                ? "line-through"
                                : "none",
                              fontWeight: item.isBought ? "600" : "500",
                              flex: 1,
                              cursor: "pointer",
                            }}
                          >
                            {item.name}
                          </span>
                        </div>
                      )}
                    </Draggable>
                  ))
                ) : (
                  <div
                    style={{
                      gridColumn: "1 / -1",
                      fontSize: "13px",
                      color: "#888",
                      textAlign: "center",
                      padding: "10px 0",
                    }}
                  >
                    ยังไม่มีรายการเตรียมของ
                  </div>
                )}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>

        <div className="no-print" style={{ marginTop: "15px" }}>
          {isAddingShop ? (
            <form
              onSubmit={handleAddShoppingItem}
              style={{
                display: "flex",
                gap: "8px",
                alignItems: "center",
                flexWrap: "wrap",
              }}
            >
              <input
                type="text"
                placeholder="พิมพ์ชื่อสิ่งของแล้วกด Enter..."
                value={newShopName}
                onChange={(e) => setNewShopName(e.target.value)}
                autoFocus
                style={{ ...inputStyle, flex: "1 1 200px" }}
              />
              <button
                type="submit"
                style={{
                  padding: "10px 20px",
                  borderRadius: "8px",
                  border: "none",
                  background: "#10b981",
                  color: "#fff",
                  fontWeight: "bold",
                  cursor: "pointer",
                }}
              >
                เพิ่ม
              </button>
              <button
                type="button"
                onClick={() => {
                  setIsAddingShop(false);
                  setNewShopName("");
                }}
                style={{
                  padding: "10px 20px",
                  borderRadius: "8px",
                  border: `1px solid ${theme?.border || "#ccc"}`,
                  background: "transparent",
                  color: theme?.text || "#666",
                  fontWeight: "bold",
                  cursor: "pointer",
                }}
              >
                ยกเลิก
              </button>
            </form>
          ) : (
            <button
              onClick={() => setIsAddingShop(true)}
              style={{
                padding: "12px",
                borderRadius: "8px",
                border: `1px dashed ${theme?.border || "#bbb"}`,
                background: "transparent",
                color: theme?.text || "#666",
                fontSize: "14px",
                fontWeight: "bold",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "8px",
                width: "100%",
                transition: "0.2s",
              }}
            >
              ➕ เพิ่มรายการเตรียมของด่วน
            </button>
          )}
        </div>
      </div>

      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "15px",
          borderTop: `1px dashed ${theme?.border || "#ccc"}`,
          paddingTop: "25px",
        }}
      >
        <h3 style={{ margin: 0, display: "flex", gap: "8px" }}>{t.summary}</h3>

        {/* 🌟 กล่องควบคุมการหารบิล (Control Panel) 🌟 */}
        <div
          className="no-print"
          style={{
            display: "flex",
            alignItems: "center",
            gap: "10px",
            background: isDarkMode ? "rgba(255,255,255,0.05)" : "#f8fafc",
            padding: "6px 12px",
            borderRadius: "8px",
            border: `1px solid ${theme?.border || "#eee"}`,
          }}
        >
          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: "6px",
              cursor: "pointer",
              fontSize: "13px",
              fontWeight: "bold",
            }}
          >
            <input
              type="checkbox"
              checked={isDivided}
              onChange={(e) => setIsDivided(e.target.checked)}
              style={{ accentColor: "#3b82f6", transform: "scale(1.2)" }}
            />
            หารค่าใช้จ่าย
          </label>

          {isDivided && (
            <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
              <span style={{ fontSize: "13px", color: "#888" }}>/</span>
              <input
                type="number"
                min="1"
                value={divideBy}
                onChange={(e) =>
                  setDivideBy(Math.max(1, parseInt(e.target.value) || 1))
                }
                style={{
                  width: "50px",
                  padding: "4px 8px",
                  borderRadius: "6px",
                  border: `1px solid ${isDarkMode ? "#555" : "#ccc"}`,
                  background: isDarkMode ? "#2c2c2c" : "#fff",
                  color: theme?.text,
                  textAlign: "center",
                  fontSize: "13px",
                  fontWeight: "bold",
                }}
              />
              <span style={{ fontSize: "13px", color: "#888" }}>คน</span>
            </div>
          )}
        </div>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
          gap: "15px",
          marginBottom: "15px",
        }}
      >
        {/* Box 1: Shared */}
        <div style={cardStyle}>
          <div
            style={{
              fontSize: "12px",
              color: "#888",
              marginBottom: "8px",
              fontWeight: "bold",
            }}
          >
            {t.sharedTitle}
          </div>
          <div
            style={{
              fontSize: "22px",
              fontWeight: "bold",
              color: "#1dd1a1",
              marginBottom: "8px",
            }}
          >
            ฿
            {sharedTotal.toLocaleString(undefined, {
              minimumFractionDigits: 2,
            })}
          </div>
          {isDivided && divideBy > 0 && sharedTotal > 0 && (
            <div
              style={{ fontSize: "12px", color: "#1dd1a1", fontWeight: "bold" }}
            >
              ตกคนละ ฿
              {(sharedTotal / divideBy).toLocaleString(undefined, {
                minimumFractionDigits: 2,
              })}
            </div>
          )}
        </div>

        {/* Box 2: Flight */}
        <div
          style={{ ...cardStyle, cursor: onNavigate ? "pointer" : "default" }}
          onClick={() => onNavigate && onNavigate("flight")}
          onMouseEnter={(e) => handleMouseEnter(e, "#1dd1a1")}
          onMouseLeave={handleMouseLeave}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              fontSize: "12px",
              color: "#888",
              marginBottom: "8px",
              fontWeight: "bold",
            }}
          >
            <span>{t.flightTitle}</span>
            <span style={{ color: "#1dd1a1" }}>{getPercent(flightTotal)}%</span>
          </div>
          <div
            style={{
              fontSize: "18px",
              fontWeight: "bold",
              marginBottom: "8px",
            }}
          >
            ฿
            {flightTotal.toLocaleString(undefined, {
              minimumFractionDigits: 2,
            })}
          </div>
          {isDivided && divideBy > 0 && flightTotal > 0 && (
            <div
              style={{ fontSize: "12px", color: "#1dd1a1", fontWeight: "bold" }}
            >
              ตกคนละ ฿
              {(flightTotal / divideBy).toLocaleString(undefined, {
                minimumFractionDigits: 2,
              })}
            </div>
          )}
        </div>

        {/* Box 3: Stay */}
        <div
          style={{ ...cardStyle, cursor: onNavigate ? "pointer" : "default" }}
          onClick={() => onNavigate && onNavigate("stay")}
          onMouseEnter={(e) => handleMouseEnter(e, "#3498db")}
          onMouseLeave={handleMouseLeave}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              fontSize: "12px",
              color: "#888",
              marginBottom: "8px",
              fontWeight: "bold",
            }}
          >
            <span>{t.stayTitle}</span>
            <span style={{ color: "#3498db" }}>{getPercent(stayTotal)}%</span>
          </div>
          <div
            style={{
              fontSize: "18px",
              fontWeight: "bold",
              marginBottom: "8px",
            }}
          >
            ฿{stayTotal.toLocaleString(undefined, { minimumFractionDigits: 2 })}
          </div>
          {isDivided && divideBy > 0 && stayTotal > 0 && (
            <div
              style={{ fontSize: "12px", color: "#3498db", fontWeight: "bold" }}
            >
              ตกคนละ ฿
              {(stayTotal / divideBy).toLocaleString(undefined, {
                minimumFractionDigits: 2,
              })}
            </div>
          )}
        </div>

        {/* Box 4: Shopping */}
        <div
          style={{ ...cardStyle, cursor: onNavigate ? "pointer" : "default" }}
          onClick={() => onNavigate && onNavigate("shopping")}
          onMouseEnter={(e) => handleMouseEnter(e, "#e74c3c")}
          onMouseLeave={handleMouseLeave}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              fontSize: "12px",
              color: "#888",
              marginBottom: "8px",
              fontWeight: "bold",
            }}
          >
            <span>{t.shoppingTitle}</span>
            <span style={{ color: "#e74c3c" }}>
              {getPercent(shoppingTotal)}%
            </span>
          </div>
          <div style={{ fontSize: "18px", fontWeight: "bold" }}>
            ฿
            {shoppingTotal.toLocaleString(undefined, {
              minimumFractionDigits: 2,
            })}
          </div>
        </div>

        {/* Box 5: Eating/Itinerary */}
        <div
          style={{ ...cardStyle, cursor: onNavigate ? "pointer" : "default" }}
          onClick={() => onNavigate && onNavigate("plan")}
          onMouseEnter={(e) => handleMouseEnter(e, "#e67e22")}
          onMouseLeave={handleMouseLeave}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              fontSize: "12px",
              color: "#888",
              marginBottom: "8px",
              fontWeight: "bold",
            }}
          >
            <span>{t.eatingTitle}</span>
            <span style={{ color: "#e67e22" }}>{getPercent(eatingTotal)}%</span>
          </div>
          <div style={{ fontSize: "18px", fontWeight: "bold" }}>
            ฿
            {eatingTotal.toLocaleString(undefined, {
              minimumFractionDigits: 2,
            })}
          </div>
        </div>

        {/* Box 6: Misc / Expense Tab */}
        <div
          style={{ ...cardStyle, cursor: onNavigate ? "pointer" : "default" }}
          onClick={() => onNavigate && onNavigate("expense")}
          onMouseEnter={(e) => handleMouseEnter(e, "#8b5cf6")}
          onMouseLeave={handleMouseLeave}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              fontSize: "12px",
              color: "#888",
              marginBottom: "8px",
              fontWeight: "bold",
            }}
          >
            <span>{t.miscTitle}</span>
            <span style={{ color: "#8b5cf6" }}>
              {getPercent(miscExpenseTotal)}%
            </span>
          </div>
          <div
            style={{
              fontSize: "18px",
              fontWeight: "bold",
              marginBottom: totalIncome > 0 ? "4px" : "0",
            }}
          >
            ฿
            {miscExpenseTotal.toLocaleString(undefined, {
              minimumFractionDigits: 2,
            })}
          </div>
          {totalIncome > 0 && (
            <div
              style={{ fontSize: "11px", color: "#2ed573", fontWeight: "bold" }}
            >
              (มีรายรับ/กองกลาง: +฿{totalIncome.toLocaleString()})
            </div>
          )}
        </div>
      </div>

      {/* Grand Total */}
      <div
        style={{
          ...cardStyle,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: "10px",
          backgroundColor:
            theme?.cardBg === "#fff" ? "#f8fafc" : "rgba(255,255,255,0.05)",
          border: `2px solid ${theme?.border || "#cbd5e1"}`,
        }}
      >
        <div>
          <div
            style={{ fontSize: "15px", fontWeight: "bold", color: theme?.text }}
          >
            💰 {t.grandTitle}
          </div>
          <div style={{ fontSize: "12px", color: "#888", marginTop: "4px" }}>
            {t.grandDesc}
          </div>
        </div>
        <div style={{ textAlign: "right" }}>
          <div
            style={{ fontSize: "24px", fontWeight: "bold", color: "#e74c3c" }}
          >
            ฿
            {grandTotal.toLocaleString(undefined, { minimumFractionDigits: 2 })}
          </div>

          {/* 🌟 ถ้ายกเลิกการหาร หรือยอดหารเป็น 1 จะไม่แสดงบรรทัดนี้ */}
          {isDivided && divideBy > 1 && (
            <div
              style={{
                fontSize: "13px",
                color: "#e74c3c",
                fontWeight: "bold",
                marginTop: "4px",
              }}
            >
              สรุปตกคนละ ฿
              {(grandTotal / divideBy).toLocaleString(undefined, {
                minimumFractionDigits: 2,
              })}
            </div>
          )}

          {totalIncome > 0 && (
            <div
              style={{
                fontSize: "13px",
                color: "#2ed573",
                fontWeight: "bold",
                marginTop: "4px",
              }}
            >
              หักกองกลางแล้ว ยอดสุทธิ ฿
              {netTotal > 0
                ? netTotal.toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                  })
                : 0}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SummaryTab;
